import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { truncateElementsToMax } from '@/features/audit/utils'
import { useDocumentStore } from '@/features/document'
import { markdownToPlainTextWithMentions } from '@/features/view-tfl-uploaded/utils/slateUtils'
import {
  ApprovalLogType,
  CommentLogType,
  CompanyLogType,
  DeliverableSetLogType,
  DocumentLogType,
  EntityAuditLogEntry,
  ProjectLogType,
  ReplyLogType,
} from '@/schema'
import {
  buildCompanyRoute,
  buildDeliverableSetDocument,
  buildDeliverableSetRoute,
  buildProjectRoute,
} from '@/utils/routes'

const LABEL_COLORS = {
  create: 'font-semibold text-green-600',
  edit: 'font-semibold text-amber-500',
  delete: 'font-semibold text-red-500 line-through',
  neutral: 'font-semibold text-primary-900',
} as const

type ActionColor = keyof typeof LABEL_COLORS

const DISPLAY_MAX = 50

const trunc = (text?: string, max: number = DISPLAY_MAX) => {
  if (!text) return ''
  return text.length > max ? `${text.slice(0, max)}…` : text
}

interface BaseChipProps {
  content?: string
  parsedContent?: Array<string | React.ReactElement>
  title?: string
  color: ActionColor
  href?: string
  state?: unknown
  onNavigate?: (state: Record<string, unknown>, href: string) => void
}

const BaseChip: React.FC<BaseChipProps> = ({
  content,
  parsedContent,
  title: externalTitle,
  color,
  href,
  state,
  onNavigate,
}) => {
  const navigate = useNavigate()

  let displayed: React.ReactNode = ''
  let title: string | undefined = externalTitle

  if (parsedContent) {
    const { elements, truncated } = truncateElementsToMax(parsedContent, DISPLAY_MAX)
    displayed = React.Children.toArray(elements)
    if (truncated && !title) {
      title = parsedContent.reduce<string>((acc, el) => {
        if (typeof el === 'string') return acc + el
        return acc + (el.props?.userName || '')
      }, '')
    }
  } else {
    const raw = content ?? ''
    displayed = trunc(raw, DISPLAY_MAX)
    if (!title && raw.length > DISPLAY_MAX) {
      title = raw
    }
  }

  let contentElement: React.ReactElement

  if (href && color !== 'delete') {
    contentElement = (
      <button
        type="button"
        className={`${LABEL_COLORS[color]} cursor-pointer hover:underline bg-transparent border-none p-0 inline-flex align-baseline text-left`}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          if (onNavigate && state) {
            onNavigate(state as Record<string, unknown>, href)
          } else {
            navigate(href, { state })
          }
        }}
      >
        {displayed}
      </button>
    )
  } else {
    contentElement = (
      <span className={LABEL_COLORS[color]}>
        {displayed}
      </span>
    )
  }

  if (title) {
    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {contentElement}
        </TooltipTrigger>
        <TooltipContent className="max-w-[400px] whitespace-normal break-words z-[100]">
          {title}
        </TooltipContent>
      </Tooltip>
    )
  }

  return contentElement
}

const NameChip: React.FC<Omit<BaseChipProps, 'content'> & { name?: string }> = ({
  name,
  ...props
}) => <BaseChip content={name ?? ''} {...props} />

const ContentChip: React.FC<Omit<BaseChipProps, 'content'> & { content?: string }> = ({
  content,
  ...props
}) => {
  const elements = content ? markdownToPlainTextWithMentions(content) : ['comment']
  return <BaseChip parsedContent={elements} {...props} />
}

const DsChip: React.FC<{
  ds: EntityAuditLogEntry['deliverable_set']
  color: ActionColor
}> = ({ ds, color }) => {
  if (!ds) return null
  return (
    <NameChip
      name={ds.deliverable_set_name}
      color={color}
      href={color === 'delete' ? undefined : buildDeliverableSetRoute(ds.id)}
    />
  )
}

const renderCompanyLog = (type: string, entry: EntityAuditLogEntry) => {
  switch (type) {
    case CompanyLogType.COMPANY_CREATED: {
      const name = entry.log_metadata?.extra?.company_name as string | undefined
      return (
        <span>
          Created a company{' '}
          {name && (
            <NameChip name={name} color="create" href={entry.company ? buildCompanyRoute(entry.company.id) : undefined} />
          )}
        </span>
      )
    }
    case CompanyLogType.COMPANY_UPDATED:
      return (
        <span>
          Edited the company{' '}
          {entry.company && (
            <NameChip name={entry.company.name} color="edit" href={buildCompanyRoute(entry.company.id)} />
          )}
        </span>
      )
    case CompanyLogType.COMPANY_DELETED:
      return (
        <span>
          Deleted the company{' '}
          {entry.company && <NameChip name={entry.company.name} color="delete" />}
        </span>
      )
    default:
      return null
  }
}

const renderProjectLog = (type: string, entry: EntityAuditLogEntry) => {
  switch (type) {
    case ProjectLogType.PROJECT_CREATED: {
      const name = entry.log_metadata?.extra?.project_name as string | undefined
      return (
        <span>
          Created a project{' '}
          {name && (
            <NameChip name={name} color="create" href={entry.project ? buildProjectRoute(entry.project.id) : undefined} />
          )}
        </span>
      )
    }
    case ProjectLogType.PROJECT_UPDATED:
      return (
        <span>
          Edited the project{' '}
          {entry.project && (
            <NameChip name={entry.project.project_name} color="edit" href={buildProjectRoute(entry.project.id)} />
          )}
        </span>
      )
    case ProjectLogType.PROJECT_DELETED:
      return (
        <span>
          Deleted the project{' '}
          {entry.project && <NameChip name={entry.project.project_name} color="delete" />}
        </span>
      )
    default:
      return null
  }
}

const renderDeliverableSetLog = (type: string, entry: EntityAuditLogEntry) => {
  const ds = entry.deliverable_set
  const meta = entry.log_metadata
  switch (type) {
    case DeliverableSetLogType.DELIVERABLE_SET_CREATED: {
      const name = meta?.extra?.deliverable_set_name as string | undefined
      return (
        <span>
          Created a deliverable set{' '}
          {name && (
            <NameChip name={name} color="create" href={ds ? buildDeliverableSetRoute(ds.id) : undefined} />
          )}
        </span>
      )
    }
    case DeliverableSetLogType.DELIVERABLE_SET_UPDATED:
      if (meta?.field === 'status' && meta.new_value === 'in_review') {
        return <span>Sent an approval request for deliverable set <DsChip ds={ds} color="edit" /></span>
      }
      return <span>Edited the deliverable set <DsChip ds={ds} color="edit" /></span>
    case DeliverableSetLogType.DELIVERABLE_SET_DELETED:
      return <span>Deleted the deliverable set <DsChip ds={ds} color="delete" /></span>
    default:
      return null
  }
}

const renderApprovalLog = (type: string, entry: EntityAuditLogEntry) => {
  const ds = entry.deliverable_set
  switch (type) {
    case ApprovalLogType.APPROVAL_REQUEST_SENT:
      return <span>Sent an approval request for deliverable set <DsChip ds={ds} color="create" /></span>
    case ApprovalLogType.APPROVAL_REQUEST_CANCELLED:
      return <span>Canceled the approval request for deliverable set <DsChip ds={ds} color="create" /></span>
    case ApprovalLogType.DELIVERABLE_SET_DELEGATED: {
      const delegateToRaw = entry.log_metadata?.extra?.delegated_to as { user_name?: string } | undefined
      const fallbackName = entry.delegate_to ? [entry.delegate_to.first_name, entry.delegate_to.last_name].filter(Boolean).join(' ') : null
      const delegateName = delegateToRaw?.user_name || fallbackName
      return (
        <span>
          Delegated deliverable set <DsChip ds={ds} color="edit" />
          {delegateName && <> to <span className="font-medium text-primary-900">{delegateName}</span></>}
        </span>
      )
    }
    default:
      return null
  }
}

const renderTflFileLog = (type: string, entry: EntityAuditLogEntry, handleDocumentNavigation: (navState: Record<string, unknown>, href: string) => void) => {
  const ds = entry.deliverable_set
  const tflName = entry.tfl_file?.name || (entry.log_metadata?.extra?.document_name)
  const docId = entry.tfl_file_id || entry.tfl_file?.id || entry.document_id

  let tflHref: string | undefined
  let tflState: unknown

  if (ds && tflName) {
    tflHref = buildDeliverableSetDocument(ds.id)
    tflState = {
      document: { id: docId, filename: tflName },
      activeDocumentId: docId,
    }
  }

  switch (type) {
    case DocumentLogType.DOCUMENT_CREATED:
      return (
        <span>
          Uploaded file{' '}
          {tflName && (
            <NameChip name={tflName} color="create" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
          )} to deliverable set <DsChip ds={ds} color="create" />
        </span>
      )
    case DocumentLogType.DOCUMENT_REVISIONED:
      return (
        <span>
          Uploaded a new version of file{' '}
          {tflName && (
            <NameChip name={tflName} color="create" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
          )} to <DsChip ds={ds} color="create" /> deliverable set
        </span>
      )
    case DocumentLogType.DOCUMENT_UPDATED:
      if (entry.log_metadata?.field === 'status') {
        return (
          <span>
            Changed status of the file{' '}
            {tflName && (
              <NameChip name={tflName} color="edit" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
            )} of deliverable set <DsChip ds={ds} color="edit" />
          </span>
        )
      }
      return (
        <span>
          Edited file{' '}
          {tflName && (
            <NameChip name={tflName} color="edit" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
          )} of deliverable set <DsChip ds={ds} color="edit" />
        </span>
      )
    case DocumentLogType.DOCUMENT_APPROVED:
      return (
        <span>
          Approved file{' '}
          {tflName && (
            <NameChip name={tflName} color="edit" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
          )} of deliverable set <DsChip ds={ds} color="edit" />
        </span>
      )
    case DocumentLogType.DOCUMENT_REJECTED:
      return (
        <span>
          Raised queries on file{' '}
          {tflName && (
            <NameChip name={tflName} color="edit" href={tflHref} state={tflState} onNavigate={handleDocumentNavigation} />
          )} of deliverable set <DsChip ds={ds} color="edit" />
        </span>
      )
    case DocumentLogType.DOCUMENT_DELETED:
      return (
        <span>
          Deleted file{' '}
          {tflName && <NameChip name={tflName} color="delete" />} of deliverable set <DsChip ds={ds} color="neutral" />
        </span>
      )
    default:
      return null
  }
}

const getThreadLogContext = (entry: EntityAuditLogEntry) => {
  const ds = entry.deliverable_set
  const meta = entry.log_metadata
  const docName = meta?.extra?.document_name
  const content = meta?.extra?.content

  let commentHref: string | undefined
  let commentState: unknown
  let documentState: unknown

  if (ds && entry.document_id) {
    commentHref = buildDeliverableSetDocument(ds.id)
    commentState = {
      document: { id: entry.document_id, filename: docName || 'File' },
      activeDocumentId: entry.document_id,
      versionId: entry.document_version_id,
      commentId: entry.comment_id,
      replyId: entry.reply_id,
    }
    documentState = {
      document: { id: entry.document_id, filename: docName || 'File' },
      activeDocumentId: entry.document_id,
      versionId: entry.document_version_id,
    }
  }

  return { ds, meta, docName, content, commentHref, commentState, documentState }
}

const renderCommentLog = (type: string, entry: EntityAuditLogEntry, handleDocumentNavigation: (navState: Record<string, unknown>, href: string) => void) => {
  const { ds, meta, docName, content, commentHref, commentState, documentState } = getThreadLogContext(entry)

  switch (type) {
    case CommentLogType.COMMENT_CREATED:
      return (
        <span>
          Added comment <ContentChip content={content} color="create" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} />
          {docName && <> on file <NameChip name={docName} color="create" href={commentHref} state={documentState} onNavigate={handleDocumentNavigation} /></>}
          {ds && <> of deliverable set <DsChip ds={ds} color="create" /></>}
        </span>
      )
    case CommentLogType.COMMENT_UPDATED: {
      const field = meta?.field
      const newValue = meta?.new_value
      let verb = 'Edited comment'
      if (field === 'status' && newValue === 'resolved') verb = 'Resolved comment'
      else if (field === 'status' && (newValue === 'open' || newValue === 'reopened')) verb = 'Reopened comment'
      return (
        <span>
          {verb} <ContentChip content={content} color="edit" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} />
          {docName && <> on file <NameChip name={docName} color="edit" href={commentHref} state={documentState} onNavigate={handleDocumentNavigation} /></>}
          {ds && <> of deliverable set <DsChip ds={ds} color="edit" /></>}
        </span>
      )
    }
    case CommentLogType.COMMENT_DELETED:
      return (
        <span>
          Deleted comment <ContentChip content={content} color="delete" />
          {docName && <> on file <NameChip name={docName} color="neutral" href={commentHref} state={documentState} onNavigate={handleDocumentNavigation} /></>}
          {ds && <> of deliverable set <DsChip ds={ds} color="neutral" /></>}
        </span>
      )
    case CommentLogType.COMMENT_ACTIONABLE:
      return (
        <span>
          Marked comment <ContentChip content={content} color="create" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} /> as <span className="font-medium text-green-600">Actionable</span>
        </span>
      )
    case CommentLogType.COMMENT_NOT_ACTIONABLE:
      return (
        <span>
          Marked comment <ContentChip content={content} color="create" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} /> as <span className="font-medium text-amber-500">Not Actionable</span>
        </span>
      )
    default:
      return null
  }
}

const renderReplyLog = (type: string, entry: EntityAuditLogEntry, handleDocumentNavigation: (navState: Record<string, unknown>, href: string) => void) => {
  const { ds, docName, content, commentHref, commentState, documentState } = getThreadLogContext(entry)

  switch (type) {
    case ReplyLogType.REPLY_CREATED:
      return (
        <span>
          Added comment <ContentChip content={content} color="create" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} />
          {docName && <> on file <NameChip name={docName} color="create" href={commentHref} state={documentState} onNavigate={handleDocumentNavigation} /></>}
          {ds && <> of deliverable set <DsChip ds={ds} color="create" /></>}
        </span>
      )
    case ReplyLogType.REPLY_UPDATED:
      return (
        <span>
          Edited comment <ContentChip content={content} color="edit" href={commentHref} state={commentState} onNavigate={handleDocumentNavigation} />
          {docName && <> on file <NameChip name={docName} color="edit" href={commentHref} state={documentState} onNavigate={handleDocumentNavigation} /></>}
          {ds && <> of deliverable set <DsChip ds={ds} color="edit" /></>}
        </span>
      )
    case ReplyLogType.REPLY_DELETED:
      return (
        <span>
          Deleted a comment
        </span>
      )
    default:
      return null
  }
}

export const HistoryAuditMessage: React.FC<{ entry: EntityAuditLogEntry }> = ({ entry }) => {
  const navigate = useNavigate()
  const { setOpenDocument, setActiveDocumentId } = useDocumentStore()

  const handleDocumentNavigation = useCallback(
    (navState: Record<string, unknown>, href: string) => {
      if (navState?.activeDocumentId && navState?.document) {
        setOpenDocument([navState.document as { id: string; filename: string }])
        setActiveDocumentId(navState.activeDocumentId as string)
      }
      navigate(href, { state: navState })
    },
    [setOpenDocument, setActiveDocumentId, navigate]
  )

  const type = entry.log_type?.toUpperCase() ?? ''

  const renderedLog =
    renderCompanyLog(type, entry) ||
    renderProjectLog(type, entry) ||
    renderDeliverableSetLog(type, entry) ||
    renderApprovalLog(type, entry) ||
    renderTflFileLog(type, entry, handleDocumentNavigation) ||
    renderCommentLog(type, entry, handleDocumentNavigation) ||
    renderReplyLog(type, entry, handleDocumentNavigation)

  if (renderedLog) {
    return renderedLog
  }

  return (
    <span className="text-secondary-500 italic">
      {entry.log_type?.toLowerCase().split('_').join(' ')}
    </span>
  )
}
